import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Select,
  DatePicker,
  Button,
  Table,
  Spin,
  Empty,
  Tabs,
  Tag,
} from 'antd';
import {
  ReloadOutlined,
  FilterOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  DollarOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import dayjs from 'dayjs';
import { fetchAnalyticsDashboardRequest, fetchAnalyticsVendorProductsRequest } from '../../redux/actions/analyticsActions';
import './Analytics.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_COLORS = {
  open: '#faad14',
  in_delivery: '#1890ff',
  delivered: '#13c2c2',
  billed: '#722ed1',
  verified: '#52c41a',
  closed: '#389e0d',
  cancelled: '#ff4d4f',
};

const CHART_COLORS = ['#0066cc', '#13c2c2', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#eb2f96'];

const formatCurrency = (val) => {
  if (val == null) return '₹0';
  return `₹${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCurrencyShort = (val) => {
  if (val == null) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${val}`;
};

const CustomTooltipCurrency = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

const ORDER_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_delivery', label: 'In Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'billed', label: 'Billed' },
  { value: 'verified', label: 'Verified' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function Analytics() {
  const dispatch = useDispatch();
  const { dashboard, loading, vendorProducts, vendorProductsLoading } = useSelector(s => s.analytics);
  const vendors = useSelector(s => s.home?.vendors || []);
  const firmNames = useSelector(s => s.firmNames?.firmNames || []);

  const [dateRange, setDateRange] = useState([null, null]);
  const [vendorId, setVendorId] = useState(null);
  const [firmName, setFirmName] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [groupBy, setGroupBy] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');

  const buildParams = useCallback(() => {
    const params = { group_by: groupBy, top_n: 10 };
    if (dateRange[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.date_to = dateRange[1].format('YYYY-MM-DD');
    if (vendorId) params.vendor_id = vendorId;
    if (firmName) params.firm_name = firmName;
    if (orderStatus) params.order_status = orderStatus;
    return params;
  }, [dateRange, vendorId, firmName, orderStatus, groupBy]);

  const loadDashboard = useCallback(() => {
    dispatch(fetchAnalyticsDashboardRequest(buildParams()));
  }, [dispatch, buildParams]);

  useEffect(() => {
    loadDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = () => {
    loadDashboard();
    if (activeTab === 'vendor-products') {
      dispatch(fetchAnalyticsVendorProductsRequest(buildParams()));
    }
  };

  const handleReset = () => {
    setDateRange([null, null]);
    setVendorId(null);
    setFirmName('');
    setOrderStatus('');
    setGroupBy('month');
    dispatch(fetchAnalyticsDashboardRequest({ group_by: 'month', top_n: 10 }));
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'vendor-products' && vendorProducts.length === 0) {
      dispatch(fetchAnalyticsVendorProductsRequest(buildParams()));
    }
  };

  const summary = dashboard?.summary;
  const topVendors = dashboard?.top_vendors || [];
  const monthlyTrend = dashboard?.monthly_trend || [];
  const topProducts = dashboard?.top_products || [];
  const expenditureTrend = dashboard?.expenditure_trend || [];
  const firmBreakdown = dashboard?.firm_breakdown || [];

  const statusPieData = summary?.status_breakdown
    ? Object.entries(summary.status_breakdown)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => ({ name: status, value: count }))
    : [];

  const vendorChartData = topVendors.slice(0, 8).map(v => ({
    name: v.vendor_name?.length > 16 ? v.vendor_name.slice(0, 16) + '…' : v.vendor_name,
    fullName: v.vendor_name,
    Amount: v.total_amount,
    Orders: v.total_orders,
  }));

  const productChartData = topProducts.slice(0, 8).map(p => ({
    name: p.product_name?.length > 16 ? p.product_name.slice(0, 16) + '…' : p.product_name,
    fullName: p.product_name,
    Amount: p.total_amount,
    Quantity: p.total_quantity,
  }));

  const monthlyChartData = monthlyTrend.map(m => ({
    name: `${m.month_name?.slice(0, 3)} ${m.year}`,
    Amount: m.total_amount,
    Tax: m.total_tax,
    Subtotal: m.subtotal,
    Orders: m.total_orders,
  }));

  const expenditureChartData = expenditureTrend.map(e => ({
    name: e.period,
    Amount: e.total_amount,
    Tax: e.total_tax,
    Subtotal: e.subtotal,
  }));

  // Vendor × Product table columns
  const vpColumns = [
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name', width: 160, ellipsis: true },
    { title: 'Product', dataIndex: 'product_name', key: 'product_name', width: 160, ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70 },
    {
      title: 'Qty', dataIndex: 'total_quantity', key: 'total_quantity', width: 80,
      render: v => Number(v).toLocaleString('en-IN'),
      sorter: (a, b) => a.total_quantity - b.total_quantity,
    },
    {
      title: 'Amount', dataIndex: 'total_amount', key: 'total_amount', width: 120,
      render: formatCurrency,
      sorter: (a, b) => a.total_amount - b.total_amount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Avg Rate', dataIndex: 'avg_rate', key: 'avg_rate', width: 100,
      render: v => formatCurrency(v),
    },
    {
      title: 'Tax', dataIndex: 'total_tax', key: 'total_tax', width: 100,
      render: formatCurrency,
    },
    {
      title: 'Discount', dataIndex: 'total_discount', key: 'total_discount', width: 100,
      render: formatCurrency,
    },
    {
      title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', width: 70,
      sorter: (a, b) => a.total_orders - b.total_orders,
    },
  ];

  const vendorTableColumns = [
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true },
    {
      title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', width: 80,
      sorter: (a, b) => a.total_orders - b.total_orders,
    },
    {
      title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount', width: 130,
      render: formatCurrency,
      sorter: (a, b) => a.total_amount - b.total_amount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Tax', dataIndex: 'total_tax', key: 'total_tax', width: 110,
      render: formatCurrency,
    },
    {
      title: 'Discount', dataIndex: 'total_discount', key: 'total_discount', width: 110,
      render: formatCurrency,
    },
    {
      title: 'Avg Order', dataIndex: 'avg_order_value', key: 'avg_order_value', width: 120,
      render: formatCurrency,
    },
    {
      title: 'Last Order', dataIndex: 'last_order_date', key: 'last_order_date', width: 110,
      render: v => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
  ];

  const productTableColumns = [
    { title: 'Product', dataIndex: 'product_name', key: 'product_name', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70 },
    {
      title: 'Qty', dataIndex: 'total_quantity', key: 'total_quantity', width: 90,
      render: v => Number(v).toLocaleString('en-IN'),
      sorter: (a, b) => a.total_quantity - b.total_quantity,
    },
    {
      title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount', width: 130,
      render: formatCurrency,
      sorter: (a, b) => a.total_amount - b.total_amount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Avg Rate', dataIndex: 'avg_rate', key: 'avg_rate', width: 100,
      render: formatCurrency,
    },
    {
      title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', width: 80,
      sorter: (a, b) => a.total_orders - b.total_orders,
    },
  ];

  const filtersApplied = dashboard?.filters_applied;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-title">
          <BarChartOutlined className="page-header-icon" />
          <div>
            <h1>Purchase Analytics</h1>
            <p>Insights across vendors, products, and spend trends</p>
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadDashboard} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="analytics-filter-bar">
        <div className="analytics-filter-row">
          <div className="analytics-filter-item">
            <label>Date Range</label>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD MMM YYYY"
              allowClear
              style={{ width: '100%' }}
            />
          </div>
          <div className="analytics-filter-item">
            <label>Vendor</label>
            <Select
              placeholder="All vendors"
              allowClear
              value={vendorId}
              onChange={setVendorId}
              showSearch
              filterOption={(input, opt) => opt.children.toLowerCase().includes(input.toLowerCase())}
              style={{ width: '100%' }}
            >
              {vendors.map(v => (
                <Option key={v.id} value={v.id}>{v.firm_name}</Option>
              ))}
            </Select>
          </div>
          <div className="analytics-filter-item">
            <label>Firm</label>
            <Select
              placeholder="All firms"
              allowClear
              value={firmName || undefined}
              onChange={v => setFirmName(v || '')}
              style={{ width: '100%' }}
            >
              {firmNames.map(f => (
                <Option key={f.id || f.firm_name} value={f.firm_name}>{f.firm_name}</Option>
              ))}
            </Select>
          </div>
          <div className="analytics-filter-item">
            <label>Order Status</label>
            <Select
              placeholder="All statuses"
              allowClear
              value={orderStatus || undefined}
              onChange={v => setOrderStatus(v || '')}
              style={{ width: '100%' }}
            >
              {ORDER_STATUSES.map(s => (
                <Option key={s.value} value={s.value}>{s.label}</Option>
              ))}
            </Select>
          </div>
          <div className="analytics-filter-item">
            <label>Trend Granularity</label>
            <Select value={groupBy} onChange={setGroupBy} style={{ width: '100%' }}>
              <Option value="day">Daily</Option>
              <Option value="week">Weekly</Option>
              <Option value="month">Monthly</Option>
              <Option value="year">Yearly</Option>
            </Select>
          </div>
          <div className="analytics-filter-actions">
            <Button type="primary" icon={<FilterOutlined />} onClick={handleApplyFilters} loading={loading}>
              Apply
            </Button>
            <Button onClick={handleReset}>Reset</Button>
          </div>
        </div>
        {filtersApplied && (filtersApplied.date_from || filtersApplied.date_to || filtersApplied.vendor_id || filtersApplied.firm_name || filtersApplied.order_status) && (
          <div className="analytics-active-filters">
            <span className="active-filters-label">Active filters:</span>
            {filtersApplied.date_from && <Tag color="blue">{`From: ${filtersApplied.date_from}`}</Tag>}
            {filtersApplied.date_to && <Tag color="blue">{`To: ${filtersApplied.date_to}`}</Tag>}
            {filtersApplied.vendor_id && <Tag color="purple">{`Vendor: ${filtersApplied.vendor_id}`}</Tag>}
            {filtersApplied.firm_name && <Tag color="green">{`Firm: ${filtersApplied.firm_name}`}</Tag>}
            {filtersApplied.order_status && <Tag color="orange">{`Status: ${filtersApplied.order_status}`}</Tag>}
          </div>
        )}
      </div>

      <Spin spinning={loading} tip="Loading analytics...">
        {/* KPI Cards */}
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-blue"><ShoppingCartOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{summary?.total_orders?.toLocaleString('en-IN') || '0'}</div>
              <div className="kpi-label">Total Orders</div>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-green"><DollarOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{formatCurrencyShort(summary?.total_amount)}</div>
              <div className="kpi-label">Total Spend</div>
              <div className="kpi-sub">{formatCurrency(summary?.total_amount)}</div>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-purple"><RiseOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{formatCurrencyShort(summary?.avg_order_value)}</div>
              <div className="kpi-label">Avg Order Value</div>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-orange"><DollarOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{formatCurrencyShort(summary?.total_tax)}</div>
              <div className="kpi-label">Total Tax</div>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-cyan"><ClockCircleOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{summary?.pending_orders?.toLocaleString('en-IN') || '0'}</div>
              <div className="kpi-label">Pending Orders</div>
              <div className="kpi-sub">Open + In Delivery</div>
            </div>
          </div>
          <div className="analytics-kpi-card">
            <div className="kpi-icon kpi-icon-success"><CheckCircleOutlined /></div>
            <div className="kpi-body">
              <div className="kpi-value">{summary?.completed_orders?.toLocaleString('en-IN') || '0'}</div>
              <div className="kpi-label">Completed Orders</div>
              <div className="kpi-sub">Verified + Closed</div>
            </div>
          </div>
        </div>

        {/* Tabs for different views */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="analytics-tabs"
          items={[
            {
              key: 'overview',
              label: <span><BarChartOutlined /> Overview</span>,
              children: (
                <div className="analytics-tab-content">
                  {/* Monthly Trend + Status Breakdown */}
                  <div className="analytics-chart-row">
                    <div className="analytics-chart-card analytics-chart-card--wide">
                      <div className="analytics-chart-header">
                        <h3>Monthly Purchase Trend</h3>
                      </div>
                      {monthlyChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={monthlyChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                            <Tooltip content={<CustomTooltipCurrency />} />
                            <Legend />
                            <Bar dataKey="Amount" fill="#0066cc" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="Tax" fill="#13c2c2" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No monthly data" className="chart-empty" />
                      )}
                    </div>
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-header">
                        <h3>Order Status</h3>
                      </div>
                      {statusPieData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie
                              data={statusPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={90}
                              paddingAngle={3}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                              labelLine={false}
                            >
                              {statusPieData.map((entry) => (
                                <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#8884d8'} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, name) => [v + ' orders', name]} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No status data" className="chart-empty" />
                      )}
                      {statusPieData.length > 0 && (
                        <div className="status-legend">
                          {statusPieData.map(s => (
                            <div key={s.name} className="status-legend-item">
                              <span className="status-dot" style={{ background: STATUS_COLORS[s.name] }} />
                              <span className="status-name">{s.name}</span>
                              <span className="status-count">{s.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expenditure Trend */}
                  <div className="analytics-chart-row">
                    <div className="analytics-chart-card analytics-chart-card--full">
                      <div className="analytics-chart-header">
                        <h3>Expenditure Trend ({groupBy})</h3>
                      </div>
                      {expenditureChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <AreaChart data={expenditureChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                            <defs>
                              <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#0066cc" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#0066cc" stopOpacity={0.02} />
                              </linearGradient>
                              <linearGradient id="colorTax" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#13c2c2" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#13c2c2" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                            <Tooltip content={<CustomTooltipCurrency />} />
                            <Legend />
                            <Area type="monotone" dataKey="Amount" stroke="#0066cc" fill="url(#colorAmount)" strokeWidth={2} />
                            <Area type="monotone" dataKey="Tax" stroke="#13c2c2" fill="url(#colorTax)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No expenditure data" className="chart-empty" />
                      )}
                    </div>
                  </div>

                  {/* Top Vendors + Top Products charts */}
                  <div className="analytics-chart-row">
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-header">
                        <h3>Top Vendors by Spend</h3>
                      </div>
                      {vendorChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={vendorChartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                            <Tooltip content={<CustomTooltipCurrency />} />
                            <Bar dataKey="Amount" radius={[0, 3, 3, 0]}>
                              {vendorChartData.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No vendor data" className="chart-empty" />
                      )}
                    </div>
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-header">
                        <h3>Top Products by Spend</h3>
                      </div>
                      {productChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={productChartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                            <Tooltip content={<CustomTooltipCurrency />} />
                            <Bar dataKey="Amount" radius={[0, 3, 3, 0]}>
                              {productChartData.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[(i + 4) % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No product data" className="chart-empty" />
                      )}
                    </div>
                  </div>

                  {/* Firm Breakdown */}
                  {firmBreakdown.length > 0 && (
                    <div className="analytics-chart-row">
                      <div className="analytics-chart-card analytics-chart-card--full">
                        <div className="analytics-chart-header">
                          <h3>Spend by Firm</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={firmBreakdown} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="firm_name" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                            <Tooltip content={<CustomTooltipCurrency />} />
                            <Bar dataKey="total_amount" name="Amount" radius={[3, 3, 0, 0]}>
                              {firmBreakdown.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'vendors',
              label: <span><TeamOutlined /> Vendors</span>,
              children: (
                <div className="analytics-tab-content">
                  <div className="modern-table-container">
                    <div className="table-header">
                      <div className="table-header-left">
                        <h3>Vendor Analytics</h3>
                        <span className="table-count">{topVendors.length} vendors</span>
                      </div>
                    </div>
                    <Table
                      dataSource={topVendors}
                      columns={vendorTableColumns}
                      rowKey="vendor_id"
                      pagination={{ pageSize: 15, showSizeChanger: false }}
                      size="middle"
                      locale={{ emptyText: <Empty description="No vendor data for selected filters" /> }}
                      scroll={{ x: 800 }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'products',
              label: <span><AppstoreOutlined /> Products</span>,
              children: (
                <div className="analytics-tab-content">
                  <div className="modern-table-container">
                    <div className="table-header">
                      <div className="table-header-left">
                        <h3>Product Analytics</h3>
                        <span className="table-count">{topProducts.length} products</span>
                      </div>
                    </div>
                    <Table
                      dataSource={topProducts}
                      columns={productTableColumns}
                      rowKey="product_id"
                      pagination={{ pageSize: 15, showSizeChanger: false }}
                      size="middle"
                      locale={{ emptyText: <Empty description="No product data for selected filters" /> }}
                      scroll={{ x: 700 }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'vendor-products',
              label: <span><BarChartOutlined /> Vendor × Product</span>,
              children: (
                <div className="analytics-tab-content">
                  <div className="analytics-vp-info">
                    <p>Detailed breakdown of every vendor–product combination. Use the filters above to narrow by date range, vendor, or product status, then click <strong>Apply</strong>.</p>
                  </div>
                  <Spin spinning={vendorProductsLoading}>
                    <div className="modern-table-container">
                      <div className="table-header">
                        <div className="table-header-left">
                          <h3>Vendor × Product Breakdown</h3>
                          <span className="table-count">{vendorProducts.length} rows</span>
                        </div>
                        <Button
                          type="primary"
                          icon={<FilterOutlined />}
                          onClick={() => dispatch(fetchAnalyticsVendorProductsRequest(buildParams()))}
                          loading={vendorProductsLoading}
                        >
                          Load / Refresh
                        </Button>
                      </div>
                      <Table
                        dataSource={vendorProducts}
                        columns={vpColumns}
                        rowKey={(r) => `${r.vendor_id}-${r.product_id}`}
                        pagination={{ pageSize: 20, showSizeChanger: false }}
                        size="middle"
                        locale={{ emptyText: <Empty description="Click 'Load / Refresh' to fetch vendor × product data" /> }}
                        scroll={{ x: 900 }}
                      />
                    </div>
                  </Spin>
                </div>
              ),
            },
          ]}
        />
      </Spin>
    </div>
  );
}

export default Analytics;
